<template>
  <div class="tw-grid tw-grid-cols-5 tw-gap-3 tw-h-96 tw-overflow-scroll tw-bg-gray-50 tw-p-2 tw-rounded">
    <div class="tw-text-sm tw-font-medium tw-text-gray-700">Agreement Start Date</div>
    <div class="tw-col-span-4 tw-text-sm tw-text-gray-700">
      This agreement is effective on the date of acceptance by the {{ computedLabelSingular }} (Customer), and includes all Plans, Credits, and respective Pricing for selected
      Plans
    </div>

    <div class="tw-text-sm tw-font-medium tw-text-gray-700">Term and End Date</div>
    <div class="tw-col-span-4 tw-text-sm tw-text-gray-700">Term ending {{ term }} from the Agreement Start Date (except for Cycle Day and/or Status adjustments).</div>

    <div class="tw-text-sm tw-font-medium tw-text-gray-700">Billing Cycle Day</div>
    <div class="tw-col-span-4 tw-text-sm tw-text-gray-700">
      Plan(s) are charged every {{ cycle }}(s) from the Agreement Start Date (except for Cycle Day and/or Status adjustments), and respective Credits are updated in your account
      once charge is processed successfully.
    </div>

    <div class="tw-text-sm tw-font-medium tw-text-gray-700">Payment Terms</div>
    <div class="tw-col-span-4 tw-text-sm tw-text-gray-700">
      All payments are processed via the Debit Card, Credit Card, or ACH specified on the account. The first payment will be processed upon the launch of your Plan(s) and may
      include additional One-Time charges. All Plan's recurring fees are due and will be processed on the same date each {{ cycle }} as your Agreement Start Date ("Cycle Day")
      unless otherwise adjusted.
    </div>

    <div class="tw-text-sm tw-font-medium tw-text-gray-700">Initial Payment</div>
    <div class="tw-col-span-4 tw-text-sm tw-text-gray-700">
      <money-format class="tw-text-sm tw-font-medium tw-text-gray-700" :value="priceCents" :locale="locale" :currency-code="currency" :subunits-value="true"></money-format>
      plus applicable taxes. Due upon acceptance and launch of Plan(s). Includes any One-Time charges.
    </div>

    <div class="tw-text-sm tw-font-medium tw-text-gray-700">Recurring Fee</div>
    <div class="tw-col-span-4 tw-text-sm tw-text-gray-700">
      <Money-Format class="tw-text-sm tw-font-medium tw-text-gray-700" :value="priceCents" :locale="locale" :currency-code="currency" :subunits-value="true"></Money-Format>
      plus applicable taxes. Plan(s) fees each {{ cycle }} based on Plan(s) selected for the respoctive Agreement Period.
    </div>

    <div v-if="downPaymentCents > 0" class="tw-text-sm tw-font-medium tw-text-gray-700">Down Payment</div>
    <div v-if="downPaymentCents > 0" class="tw-col-span-4 tw-text-sm tw-text-gray-700">
      <Money-Format class="tw-text-sm tw-font-medium tw-text-gray-700" :value="downPaymentCents" :locale="locale" :currency-code="currency" :subunits-value="true"></Money-Format>
      plus applicable taxes. This plan requires a one time down payment due upon acceptance and launch of Plan(s).
    </div>

    <div v-if="serviceFeeCents > 0" class="tw-text-sm tw-font-medium tw-text-gray-700">Service Fee</div>
    <div v-if="serviceFeeCents > 0" class="tw-col-span-4 tw-text-sm tw-text-gray-700">
      <Money-Format class="tw-text-sm tw-font-medium tw-text-gray-700" :value="serviceFeeCents" :locale="locale" :currency-code="currency" :subunits-value="true"></Money-Format>
      This plan includes a service fee of during each billing cycle.
    </div>

    <div class="tw-text-sm tw-font-medium tw-text-gray-700">Declined Payments</div>
    <div class="tw-col-span-4 tw-text-sm tw-text-gray-700">
      A Declined charge will place your Plan(s) in "Past-Due" status and a $10 Declined fee may be charged each week until payment is successful. No Credits can be used while the
      Plan is in "Past-Due" status. Upon successful payment, outstanding charges will be processed and Credits will be applied to bring your account current.
    </div>

    <div class="tw-text-sm tw-font-medium tw-text-gray-700">Auto-Renewal Terms</div>
    <div class="tw-col-span-4 tw-text-sm tw-text-gray-700">
      In order to avoid service disruption of your Plan(s), at the end of the Agreement Term this agreement will automatically renew for a period of another Agreement Term unless
      modified or cancelled by written notice (email or letter) from {{ computedLabelSingular }} (Customer) to {{ businessName }} prior to the current Agreement End Date. Plan(s)
      fees may increase by up to 10% upon renewal without notification.
    </div>

    <div class="tw-text-sm tw-font-medium tw-text-gray-700">Non-Renewal Terms</div>
    <div class="tw-col-span-4 tw-text-sm tw-text-gray-700">
      For Plan(s) cancelled at the end of their current Agreement Period, the {{ computedLabelSingular }} will have 60 days from the current Agreement End Date to use any remaining
      Credits on their account for the respective Plan(s). Respective Credits are voided and the Plans(s) are terminated 60 days after the current Agreement End Date.
    </div>

    <div class="tw-text-sm tw-font-medium tw-text-gray-700">Early Termination</div>
    <div class="tw-col-span-4 tw-text-sm tw-text-gray-700">
      For any termination by the {{ computedLabelSingular }} before the current Agreement End Date, all remaining fees for the current Agreement Period will be charged in full
      (100%) and Credits will be applied to their account. The {{ computedLabelSingular }} will have 60 days from the Early Termination End Date to use the respective Credits. The
      respective Credits are voided and the Plan(s) are terminated 60 days after the Early Termination End Date. The only exception is, when documented, that the
      {{ computedLabelSingular }} is moving more than 50 miles away from the nearest location.
    </div>
  </div>
</template>
<script>
import MoneyFormat from "vue-money-format";
import { mapGetters } from "vuex";

export default {
  name: "BusinessPlanAgreement",
  components: { MoneyFormat },
  props: {
    businessPlanId: String,
    priceCents: Number,
    downPaymentCents: Number,
    currency: String,
    businessName: String,
    termLength: Number,
    termInterval: String,
    cycleFrequency: Number,
    cycleInterval: String,
    startDate: String,
    serviceFeeCents: Number,
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    computedLabelSingular() {
      if (!this.organization || !this.organization.customerLabelSingular) {
        return "patient"; // Fallback value
      }
      return this.organization.customerLabelSingular;
    },
    term() {
      return this.termLength > 1 ? this.termLength + " " + this.termInterval + "s" : this.termLength + " " + this.termInterval;
    },
    cycle() {
      return this.cycleFrequency > 1 ? this.cycleFrequency + " " + this.cycleInterval + "s" : this.cycleFrequency + " " + this.cycleInterval;
    },
    locale() {
      return window.navigator.language;
    },
  },
};
</script>
